import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui';
import { Icon } from '../../../../components/icons';
import {
  v2FamilyAccounts,
  type FamilyAccount, type FamilyAccountInput,
} from '../../../../lib/familyAccounts';
import { type V1ProfileOption } from '../../../../lib/v1';
import { fieldOptions } from '../../profile/options';
import { Field, Select, CheckGroup } from '../../profile/controls';
import { FAMILY_ACCOUNT_FIELD_LABEL_KEYS, isPetYes } from '../../../../lib/familyAccountCompleteness';

const toStrArr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

export function WohnenForm({
  account, dark, opts, onSaved,
}: {
  account: FamilyAccount;
  dark: boolean;
  opts: Record<string, V1ProfileOption[]> | null;
  onSaved: (a: FamilyAccount) => void;
}) {
  const { t: tr } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const opt = (k: string) => fieldOptions(k, opts);

  const [wohnsituation, setWohnsituation] = useState<string>(account.wohnsituation ?? '');
  const [wohnort, setWohnort] = useState<string>(account.wohnort ?? '');
  // Altbestand (Backfill aus CRM-1.0-Profilen) liefert die Options-IDs als
  // Zahlen ([91,93]). Ungewandelt zurückgeschickt lehnt der Server sie ab
  // („wohnzustand.0 muss Text sein"), und die Checkboxen vergleichen per
  // includes() mit String-IDs — die Auswahl erschien deshalb auch leer.
  const [wohnzustand, setWohnzustand] = useState<string[]>(toStrArr(account.wohnzustand));
  const [haustiere, setHaustiere] = useState<string>(account.haustiere ?? '');
  const [haustiereWelche, setHaustiereWelche] = useState<string[]>(toStrArr(account.haustiere_welche));
  const [einkauf, setEinkauf] = useState<string>(account.einkaufsmoeglichkeiten ?? '');
  const [raucherhaushalt, setRaucherhaushalt] = useState<string>(account.raucherhaushalt ?? '');
  const [bkWohnen, setBkWohnen] = useState<string[]>(toStrArr(account.bk_wohnen));
  // Erst nach einem Speicherversuch markieren (kein Rot beim bloßen Öffnen).
  const [submitted, setSubmitted] = useState(false);

  // Haustiere = Ja => Haustier-Felder sind Pflicht (Server prüft ebenfalls).
  const petYes = isPetYes(haustiere, opts);
  const petMissing = petYes && haustiereWelche.length === 0;
  const petError = submitted && petMissing;

  const save = async () => {
    if (saving) return;
    setSubmitted(true);
    if (petMissing) {
      setMsg(null);
      setError(tr('pflege.familienkonto.detail.wohnen.pet_required'));
      return;
    }
    setSaving(true);
    setError(null);
    setMsg(null);
    const payload: FamilyAccountInput = {
      wohnsituation: wohnsituation || null,
      wohnort: wohnort || null,
      wohnzustand,
      haustiere: haustiere || null,
      haustiere_welche: haustiereWelche,
      einkaufsmoeglichkeiten: einkauf || null,
      raucherhaushalt: raucherhaushalt || null,
      bk_wohnen: bkWohnen,
    };
    try {
      const result = await v2FamilyAccounts.update(account.id, payload);
      setMsg(tr('pflege.familienkonto.detail.wohnen.saved'));
      onSaved(result);
    } catch (e) {
      const err = e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } };
      setError(err.response?.data?.errors?.[0]?.message ?? err.response?.data?.message ?? (e instanceof Error ? e.message : tr('pflege.edit.save_failed')));
    } finally {
      setSaving(false);
    }
  };

  const RAUCHER_OPTS = [
    { value: '', label: '—' },
    { value: 'ja', label: tr('common.yes') },
    { value: 'nein', label: tr('common.no') },
  ];

  const missing = (account.completeness?.missing ?? []).filter(k => FAMILY_ACCOUNT_FIELD_LABEL_KEYS[k]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {missing.length > 0 && (
        <div data-testid="wohnen-missing" style={{ fontSize: 12, color: '#b54708', fontWeight: 600 }}>
          {tr('pflege.familienkonto.detail.wohnen.missing', {
            fields: missing.map(k => tr(FAMILY_ACCOUNT_FIELD_LABEL_KEYS[k])).join(', '),
          })}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field dark={dark} label={tr('pflege.form.field.wohnsituation')}>
          <Select dark={dark} value={wohnsituation} onChange={setWohnsituation} placeholder="—" options={opt('wohnsituation')} />
        </Field>
        <Field dark={dark} label={tr('pflege.form.field.wohnort')}>
          <Select dark={dark} value={wohnort} onChange={setWohnort} placeholder="—" options={opt('wohnort')} />
        </Field>
        <Field dark={dark} label={tr('pflege.form.field.haustiere')}>
          <Select dark={dark} value={haustiere} onChange={setHaustiere} placeholder="—" options={opt('haustiere')} />
        </Field>
        <Field dark={dark} label={tr('pflege.form.field.einkaufsmoeglichkeiten')}>
          <Select dark={dark} value={einkauf} onChange={setEinkauf} placeholder="—" options={opt('einkaufsmoeglichkeiten')} />
        </Field>
        <Field dark={dark} label={tr('pflege.familienkonto.detail.wohnen.raucherhaushalt')}>
          <Select dark={dark} value={raucherhaushalt} onChange={setRaucherhaushalt} placeholder="—" options={RAUCHER_OPTS} />
        </Field>
      </div>
      <Field dark={dark} label={tr('pflege.familienkonto.detail.wohnen.wohnzustand')}>
        <CheckGroup dark={dark} value={wohnzustand} onChange={setWohnzustand} options={opt('wohnzustand')} cols={2} />
      </Field>
      <Field
        dark={dark}
        label={tr('pflege.familienkonto.detail.wohnen.haustiere_welche')}
        required={petYes}
        hasError={petError}
      >
        <CheckGroup dark={dark} value={haustiereWelche} onChange={setHaustiereWelche} options={opt('haustiere_welche')} cols={3} />
      </Field>
      <Field dark={dark} label={tr('pflege.form.field.bk_wohnen')} hint={tr('pflege.form.hint.multi')}>
        <CheckGroup dark={dark} value={bkWohnen} onChange={setBkWohnen} options={opt('bk_wohnen')} cols={2} />
      </Field>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button variant="primary" size="sm" dark={dark} onClick={save} disabled={saving} icon={<Icon.Check c="#fff" />}>
          {saving ? tr('settings.lead_quellen.saving') : tr('pflege.familienkonto.detail.wohnen.save_button')}
        </Button>
        {msg && <span style={{ fontSize: 12, color: '#039855', fontWeight: 600 }}>{msg}</span>}
        {error && <span style={{ fontSize: 12, color: '#b42318', fontWeight: 600 }}>{error}</span>}
      </div>
    </div>
  );
}
